import React from 'react';
import styled from 'styled-components';
import CloudUpload from '@material-ui/icons/CloudUpload';
import { getFileExtension } from '../utils/franchiseHelpers';

const MAX_BYTES = 2 * 1024 * 1024;

interface Props {
  initialValues?: {
    qrcBase64?: string;
    qrc?: string;
  };
  editable?: boolean;
  onFileSelect?: (file: File | null) => void;
}

const FranchiseQRCode = ({
  initialValues,
  editable = false,
  onFileSelect,
}: Props) => {
  const base64 = initialValues?.qrcBase64 || '';
  const fileType = getFileExtension(initialValues?.qrc) || '';

  const qrUrl =
    typeof initialValues?.qrc === 'string' &&
    /^(https?:\/\/|\/)/.test(initialValues.qrc)
      ? initialValues.qrc
      : '';

  const initialPreview = base64 && fileType
    ? `data:image/${fileType};base64,${base64}`
    : qrUrl;

  const [localPreview, setLocalPreview] = React.useState<string>('');
  const [localName, setLocalName] = React.useState<string>('');
  const [error, setError] = React.useState<string>('');
  const [failed, setFailed] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const previewSrc = localPreview || initialPreview;

  React.useEffect(() => {
    setFailed(false);
  }, [previewSrc]);

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('File must be an image');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Image must be less than 2 MB');
      return;
    }
    setError('');
    const reader = new FileReader();
    reader.onload = () => {
      setLocalPreview(String(reader.result || ''));
      setLocalName(file.name);
    };
    reader.readAsDataURL(file);
    if (onFileSelect) onFileSelect(file);
  };

  const onPick = () => inputRef.current?.click();

  const showImage = Boolean(previewSrc) && !failed;

  return (
    <Wrapper>
      <Label>Payment QR Code</Label>
      <Frame
        $editable={editable}
        onClick={editable ? onPick : undefined}
        onDragOver={e => {
          if (!editable) return;
          e.preventDefault();
          e.currentTarget.setAttribute('data-drag', 'true');
        }}
        onDragLeave={e => {
          e.currentTarget.removeAttribute('data-drag');
        }}
        onDrop={e => {
          if (!editable) return;
          e.preventDefault();
          e.currentTarget.removeAttribute('data-drag');
          handleFile(e.dataTransfer.files?.[0]);
        }}
      >
        {showImage ? (
          <Preview
            src={previewSrc}
            alt="Payment QR code"
            onError={() => setFailed(true)}
          />
        ) : (
          <EmptyHint>
            {editable ? (
              <>
                <CloudUpload fontSize="small" />
                <span>Click or drop image</span>
              </>
            ) : (
              'No QR code available'
            )}
          </EmptyHint>
        )}
        {editable && showImage && (
          <Overlay>
            <CloudUpload fontSize="small" />
            <span>Replace</span>
          </Overlay>
        )}
        <HiddenInput
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={e => {
            handleFile(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </Frame>
      {localName && <FileName>{localName}</FileName>}
      {error && <ErrorText>{error}</ErrorText>}
    </Wrapper>
  );
};

export default FranchiseQRCode;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const Label = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
`;

const Frame = styled.div<{ $editable: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 130px;
  border: 1px ${({ $editable }) => ($editable ? 'dashed' : 'solid')} var(--border);
  border-radius: 8px;
  background: var(--neutral-50);
  box-sizing: border-box;
  overflow: hidden;
  cursor: ${({ $editable }) => ($editable ? 'pointer' : 'default')};
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    ${({ $editable }) =>
      $editable &&
      `
      border-color: var(--primary-400);
      background: var(--primary-50);
    `}
  }

  &[data-drag='true'] {
    border-color: var(--primary-600);
    background: var(--primary-50);
  }
`;

const Preview = styled.img`
  max-width: 108px;
  max-height: 108px;
  object-fit: contain;
`;

const EmptyHint = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: var(--neutral-500);
  text-align: center;
  padding: 0 8px;
`;

const Overlay = styled.div`
  position: absolute;
  inset: auto 0 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 0;
  font-size: 0.75rem;
  color: var(--neutral-0);
  background: rgba(0, 0, 0, 0.55);
  opacity: 0;
  transition: opacity 0.15s ease;

  ${Frame}:hover & {
    opacity: 1;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const FileName = styled.div`
  font-size: 0.75rem;
  color: var(--neutral-600);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ErrorText = styled.div`
  font-size: 0.75rem;
  color: var(--danger-500);
`;
